import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
  CONFIG_ADMIN_USER_TYPE,
  MEMBER_USER_TYPE, purple, textGrey,
} from 'consts';
import { Context } from 'context/Context';
import {
  EnelXGuiAtoms,
  EnelXGuiComponents,
} from 'gui';

import MainComment from 'components/MainComment';

const { MigrationIcon } = EnelXGuiAtoms;

const {
  MigrationLoader,
  MigrationMainSidebar,
} = EnelXGuiComponents;

const { MigrationButton } = EnelXGuiAtoms;

const initialConfig = {
  toShow: 'firstStep',
  title: 'newIssue',
};

const optionStyle = `
flex
items-center
w-full border rounded p-3 cursor-pointer justify-between border-custom-paleGrey`;

export const ticketConfig = (t) => [
  {
    id: '1',
    title: t('ticket.ticket1'),
    CaseReason: 'XCMOT001',
    CaseSubCause: 'ATCL052',
    CaseSubType: 'XC120',
  },
  {
    id: '2',
    title: t('ticket.ticket2'),
    CaseReason: 'XCMOT004',
    CaseSubCause: 'ATCL048',
    CaseSubType: 'XC340',
  },
  {
    id: '3',
    title: t('ticket.ticket3'),
    CaseReason: 'XCMOT002',
    CaseSubCause: 'ATCL003',
    CaseSubType: '-',
  },
  {
    id: '4',
    title: t('ticket.ticket4'),
    CaseReason: 'XCMOT008',
    CaseSubCause: 'ATCL154',
    CaseSubType: 'XC381',
  },
  {
    id: '5',
    title: t('ticket.ticket5'),
    CaseReason: 'XCMOT004',
    CaseSubCause: 'ATCL048',
    CaseSubType: 'XC345',
  },
  {
    id: '6',
    title: t('ticket.ticket6'),
    shortTitle: t('ticket.ticket6Short'),
    CaseReason: 'XCMOT005',
    CaseSubCause: 'ATCL041',
    CaseSubType: 'XC353',
  },
  {
    id: '7',
    title: t('ticket.ticket7'),
    CaseReason: 'XCMOT005',
    CaseSubCause: 'ATCL043',
    CaseSubType: 'XC339',
  },
  {
    id: '8',
    title: t('ticket.ticket8'),
    CaseReason: 'XCMOT005',
    CaseSubCause: 'ATCL043',
    CaseSubType: 'XC348',
  },
  {
    id: '9',
    title: t('ticket.ticket9'),
    CaseReason: 'XCMOT005',
    CaseSubCause: 'ATCL041',
    CaseSubType: 'XC346',
  },
  {
    id: '10',
    title: t('ticket.ticket10'),
    CaseReason: 'XCMOT009',
    CaseSubCause: 'ATCL055',
    CaseSubType: '-',
  },
  {
    id: '11',
    title: t('ticket.ticket11'),
    CaseReason: 'XCMOT008',
    CaseSubCause: 'ATCL099',
    CaseSubType: 'XC338',
  },
  {
    id: '12',
    title: t('ticket.ticket12'),
    CaseReason: 'XCMOT001',
    CaseSubCause: 'ATCL033',
    CaseSubType: 'XC042',
  },
];

const Ticket = () => {
  const { t } = useTranslation();

  const {
    defaultQueryProps,
    api,
    user: {
      firstName, lastName,
    },
    userType,
    ticketInfo,
  } = useContext(Context);

  const [renderConfig, setRenderConfig] = useState({
    toShow: '',
    title: '',
  });

  const [ticket, setTicket] = useState({
    CaseReason: '',
    CaseSubCause: '',
    CaseSubType: '',
    Description: '',
  });

  const {
    ecName,
    configName,
    podName,
    accountId,
    segment,
  } = ticketInfo ?? {};

  const firstStep = ticketConfig(t).map(({
    id, title, shortTitle, CaseReason, CaseSubCause, CaseSubType,
  }) => (
    <div
      onClick={() => {
        setRenderConfig({
          toShow: 'secondStep',
          title: shortTitle ?? title,
        });
        setTicket({
          CaseReason,
          CaseSubCause,
          CaseSubType,
        });
      }}
      className={optionStyle}
      key={id}
    >
      <span>{t(title)}</span>
      <MigrationIcon
        name="arrow-right"
        size="1em"
        color={textGrey}
      />
    </div>
  ));

  const secondStep = () => (
    <>
      <div
        onClick={() => {
          setRenderConfig(initialConfig);
          setTicket({});
        }}
        className="flex items-center gap-2 cursor-pointer mb-2"
      >
        <MigrationIcon
          name="arrow-left"
          size="1em"
          color={purple}
        />
        <p className="text-primary-purple hover:underline">{t('back')}</p>
      </div>
      <p>
        <strong>{t('dashboard:filters.energyCommunity')}</strong>
        {`: ${ecName ?? '-'}`}
      </p>
      {userType === CONFIG_ADMIN_USER_TYPE ? (
        <p>
          <strong>{t('dashboard:filters.configuration')}</strong>
          {`: ${configName ?? '-'}`}
        </p>
      ) : null}
      {userType === MEMBER_USER_TYPE ? (
        <p>
          <strong>{t('dashboard:filters.loadArea')}</strong>
          {`: ${podName ?? '-'}`}
        </p>
      ) : null}
      <MainComment
        label={t('issueDescription')}
        placeholder={t('issueDescription')}
        onChange={(value) => setTicket({
          ...ticket,
          Description: value,
        })}
      />
    </>
  );

  const toShowMapper = {
    firstStep,
    secondStep: secondStep(),
  };

  const render = () => (
    <div className="flex flex-col gap-2">
      {toShowMapper[renderConfig.toShow]}
    </div>
  );

  const businessLineMapper = {
    B2C: 'eHome',
    B2B: 'eIndustry',
    B2G: 'eCity',
  };

  const description = `
  First name: ${firstName},
  Last name: ${lastName},
  Ec name: ${ecName},
  Configuration: ${configName},
  ${userType === MEMBER_USER_TYPE ? `POD: ${podName},` : ''}
  Description: ${ticket.Description}
  `;

  const { isFetching: isCreatingTicket, refetch: createTicket } = useQuery(
    ['createTicket'],
    () => api.ticket.createTicket({
      Case: {
        ...ticket,
        Description: description,
        Priority: '0',
        Status: 'ESTA001',
        Channel: 'EnergyCommunity',
        Country: 'Italy',
        AccountID: accountId,
        LegalEntity: 'EnelXItalia',
        Segment: segment,
        BusinessLine: businessLineMapper[segment],
      },
    }),
    {
      ...defaultQueryProps,
      enabled: false,
    },
  );

  const closeSidebar = () => setRenderConfig({
    toShow: '',
  });

  return (
    <>
      {isCreatingTicket ? <MigrationLoader /> : null}
      <div className="fixed bottom-7 right-7">
        <MigrationButton
          iconLibrary="material"
          iconName="chat"
          iconSize="1.5em"
          btnSize="default"
          onClick={() => setRenderConfig(initialConfig)}
        />
      </div>
      <MigrationMainSidebar
        title={t(renderConfig.title)}
        close={closeSidebar}
        submit={renderConfig.toShow === 'secondStep' ? () => {
          createTicket();
          closeSidebar();
        } : null}
        buttonDisabled={!ticket.Description}
        submitText={t('send')}
        render={render}
        isShown={!!renderConfig.toShow}
      />
    </>
  );
};

export default Ticket;
